import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { DateUtil, isNullOrUndefined } from '@uzurv/core';
// tslint:disable-next-line:ordered-imports
import { UZURV_REST_CONFIGURATION, UzurvRestConfiguration, UzurvRestService } from '@uzurv/http';
import { Deserialize, Serialize } from 'cerialize';
import moment from 'moment-timezone';
import { Observable, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import {
  ApprovalObjectStatus,
  ClientFeeSchedule,
  ClientFeeScheduleApprovalStatus,
  FareCalculator,
  Geofence,
  HolidaySchedule,
  InterestedTransitProgramDriver,
  MiniDriver,
  OperatingSchedule,
  RideMetadataField,
  RideRequestInfo,
  TransitProgram,
  TransitProgramDriver,
  TransitProgramProvider,
  TransitProgramVehicleInspectionItem,
  VehicleType,
  ApprovalObjectInfo,
  ClientFeeScheduleApprovalObjectInfo
} from '../../models';
import { ApprovalRequestSubmission } from '../../models/approval-request-submission';
import { IGeoCoords } from '../maps';

@Injectable({
  providedIn: 'root'
})
export class TransitProgramService extends UzurvRestService {
  readonly resourceUrl = 'transitPrograms/';

  constructor(
    httpClient: HttpClient,
    @Inject(UZURV_REST_CONFIGURATION)
    protected uzurvRestConfiguration: UzurvRestConfiguration,
    domSanitizer: DomSanitizer
  ) {
    super(httpClient, uzurvRestConfiguration, domSanitizer);
  }

  /**
   * Gets a page of transit programs
   *
   * @param pageNumber
   * @param pageSize
   * @param includeInactive -optional includes  inactive transit programs
   * @permission transit_program_read
   */
  getTransitPrograms(pageNumber: number, pageSize: number, includeInactive?: boolean): Observable<Array<TransitProgram>> {
    return this.get<Array<TransitProgram>>(this.resourceUrl, {
      pageNumber: pageNumber,
      pageSize: pageSize,
      includeInactive: includeInactive
    }).pipe(map(response => Deserialize(response, TransitProgram)));
  }

  /**
   * Returns transit programs in a given radius from a zip code
   *
   * @param zipCode
   * @param radiusMiles
   * @param pageNumber
   * @param pageSize
   * @permission driver
   */
  getTransitProgramsByZipCode(
    zipCode: number,
    radiusMiles: number,
    pageNumber: number = 0,
    pageSize: number = 20
  ): Observable<Array<TransitProgram>> {
    return this.get<Array<TransitProgram>>(this.resourceUrl, {
      zipCode: zipCode,
      radiusMiles: radiusMiles,
      pageNumber: pageNumber,
      pageSize: pageSize
    }).pipe(map(response => Deserialize(response, TransitProgram).sort((a, b) => a.name.localeCompare(b.name))));
  }

  /**
   * Returns a transit program by ID
   *
   * @param id
   */
  findTransitProgramById(id: number): Observable<TransitProgram> {
    return this.get(`${this.resourceUrl}${id}`).pipe(map(response => Deserialize(response, TransitProgram)));
  }

  /**
   * PUTs on a transit program and returns the modified transit program
   *
   * @param transitProgram
   * @permission organization_management
   */
  updateTransitProgram(transitProgram: TransitProgram): Observable<TransitProgram> {
    return this.put<TransitProgram>(`${this.resourceUrl}${transitProgram.id}`, transitProgram).pipe(
      map(response => Deserialize(response, TransitProgram))
    );
  }

  /**
   * GETs active providers for a transit program.
   *
   * @param transitProgramId
   * @param serviceLevelCode
   */
  listActiveTransitProgramProvidersForTransitProgramAndServiceLevelCode(
    transitProgramId: number,
    serviceLevelCode: string
  ): Observable<Array<TransitProgramProvider>> {
    return this.get(`${this.resourceUrl}${transitProgramId}/providers/`, {
      serviceLevelCode: serviceLevelCode
    }).pipe(map(response => Deserialize(response, TransitProgramProvider)));
  }

  /**
   * Copies the pool of transit program drivers from a source transit program to
   * a destination transit program
   *
   * @param sourceTransitProgramId
   * @param destinationTransitProgramId
   * @permission driver_management
   */
  copyTransitProgramDriversToTransitProgram(sourceTransitProgramId: number, destinationTransitProgramId: number): Observable<boolean> {
    return this.post<boolean>(`${this.resourceUrl}${sourceTransitProgramId}/duplicateDriverPool/${destinationTransitProgramId}`, null);
  }

  /**
   * Returns a list of transit program drivers by transit program
   *
   * @param transitProgramId
   * @param pageNumber
   * @param pageSize
   * @param parameters
   * @permission driver_management
   */
  getTransitProgramDriversByTransitProgramId(
    transitProgramId: number,
    pageNumber: number,
    pageSize: number,
    parameters = {}
  ): Observable<Array<TransitProgramDriver>> {
    return this.get<Array<TransitProgramDriver>>(`${this.resourceUrl}${transitProgramId}/drivers/`, {
      ...parameters,
      pageNumber: pageNumber,
      pageSize: pageSize
    }).pipe(map(response => Deserialize(response, TransitProgramDriver)));
  }

  /**
   * Returns a list of drivers withing a given radius to the provided transit program
   *
   * @param transitProgramCode
   * @param radius
   * @param pageNumber
   * @param pageSize
   * @permission ride_monitor
   */
  getWithinRadiusOfTransitProgram(
    transitProgramCode: string,
    radius: number,
    pageNumber = 0,
    pageSize = 20
  ): Observable<Array<MiniDriver>> {
    return this.get(`${this.resourceUrl}withinRadius/`, {
      transitProgramCode: transitProgramCode,
      radiusMiles: radius,
      pageNumber: pageNumber,
      pageSize: pageSize
    }).pipe(map(response => Deserialize(response, MiniDriver)));
  }

  /**
   * Returns a transit program driver by ID
   *
   * @param driverId
   * @param transitProgramId
   * @permission driver_management
   */
  findTransitProgramDriver(driverId: number, transitProgramId: number): Observable<TransitProgramDriver> {
    return this.get(`${this.resourceUrl}${transitProgramId}/drivers/${driverId}`).pipe(
      map(response => Deserialize(response, TransitProgramDriver))
    );
  }

  /**
   * Returns a list of interested drivers by ride request
   *
   * @param transitProgramId
   * @param rideRequestId
   */
  getInterestedTransitProgramDriversByRideRequest(
    transitProgramId: number,
    rideRequestId: number
  ): Observable<Array<InterestedTransitProgramDriver>> {
    return this.get<Array<InterestedTransitProgramDriver>>(
      `${this.resourceUrl}${transitProgramId}/rideRequests/${rideRequestId}/drivers/`
    ).pipe(map(response => Deserialize(response, InterestedTransitProgramDriver)));
  }

  /**
   * Returns information for a potential ride request given the supplied parameters
   *
   * @param transitProgramId
   * @param pickupLatitude
   * @param pickupLongitude
   * @param dropoffLatitude
   * @param dropoffLongitude
   * @param billablePassengerCount
   * @param pickupDate
   * @permission ride_request_read
   */
  getTransitProgramRideRequestInfo(
    transitProgramId: number,
    pickupLatitude: number,
    pickupLongitude: number,
    dropoffLatitude: number,
    dropoffLongitude: number,
    billablePassengerCount: number,
    pickupDate: Date,
    dropoffDate: Date,
    serviceLevelId: number
  ): Observable<RideRequestInfo> {
    const params = {
      pickupLatitude: pickupLatitude,
      pickupLongitude: pickupLongitude,
      dropoffLatitude: dropoffLatitude,
      dropoffLongitude: dropoffLongitude,
      billablePassengerCount: billablePassengerCount,
      serviceLevelId: serviceLevelId
    };

    if (!isNullOrUndefined(pickupDate)) {
      params['pickupDate'] = DateUtil.getFormattedDateInUtc(pickupDate, 'YYYY-MM-DD HH:mm:ss');
    }

    if (!isNullOrUndefined(dropoffDate)) {
      params['dropoffDate'] = DateUtil.getFormattedDateInUtc(dropoffDate, 'YYYY-MM-DD HH:mm:ss');
    }

    return this.get<RideRequestInfo>(`${this.resourceUrl}${transitProgramId}/rideRequestInfo/`, params).pipe(
      map(response => Deserialize(response, RideRequestInfo))
    );
  }

  /**
   * @whatItDoes Implements a stream of visible drivers that are visible in a given coordinate diagonal (the
   * 'viewBounds').
   *
   * @howToUse In your view, create an Observable subject which streams changes to your bounds. A common use case
   * is next-ing on the 'idle' event listener provided by a Google Maps view. When the view is moved, provide the
   * updated viewBounds and this service method will return visible drivers in that coordinate diagonal
   *
   * @param transitProgramId the ID of the transit program to filter drivers by
   * @param viewBounds an Observable Subject on the view which should stream the view bounds
   * you want to search in. If this is being used as a one time search, be sure to implement as
   * a ReplaySubject
   * @param until an observable that emits when the subscription should terminate
   * @permission driver_management
   */
  streamTransitProgramDriversInCoordinateDiagonal(
    transitProgramId: number,
    viewBounds: Subject<{ southWest: IGeoCoords; northEast: IGeoCoords }>,
    until: Subject<boolean>
  ): Subject<Array<MiniDriver>> {
    const visibleDrivers: Subject<Array<MiniDriver>> = new Subject();

    viewBounds.pipe(takeUntil(until)).subscribe(coordinates => {
      this.get('transitPrograms/' + transitProgramId + '/drivers/', {
        upperLatitude: coordinates.northEast.lat,
        leftLongitude: coordinates.southWest.lng,
        lowerLatitude: coordinates.southWest.lat,
        rightLongitude: coordinates.northEast.lng
      }).subscribe(
        drivers => {
          visibleDrivers.next(drivers);
        },
        err => {
          visibleDrivers.error(err);
        }
      );
    });

    return visibleDrivers;
  }

  /**
   * Returns search results for transit programs on name, code,
   * and ID (exact match only)
   *
   * @param searchString
   * @param pageNumber
   * @param pageSize
   * @param includeInactive -optional includes  inactive transit programs
   * @permission transit_program_search
   */
  searchTransitPrograms(
    searchString: string,
    pageNumber: number,
    pageSize: number,
    includeInactive?: boolean
  ): Observable<Array<TransitProgram>> {
    return this.get<Array<TransitProgram>>(`search/${this.resourceUrl}`, {
      searchString: searchString,
      pageNumber: pageNumber,
      pageSize: pageSize,
      includeInactive: includeInactive
    }).pipe(map(response => Deserialize(response, TransitProgram)));
  }

  /**
   * @param id
   * @permission transit_program_read
   */
  listOperatingSchedulesForTransitProgram(id: number): Observable<Array<OperatingSchedule>> {
    return this.get<Array<OperatingSchedule>>(`${this.resourceUrl}${id}/operatingSchedules/`).pipe(
      map(response => Deserialize(response, OperatingSchedule))
    );
  }

  /**
   * Creates a new transit program driver
   *
   * @param transitProgramDriver
   * @permission driver_management
   */
  createTransitProgramDriver(transitProgramDriver: TransitProgramDriver): Observable<TransitProgramDriver> {
    return this.post(`${this.resourceUrl}${transitProgramDriver.transitProgramId}/drivers/`, Serialize(transitProgramDriver)).pipe(
      map(response => Deserialize(response, TransitProgramDriver))
    );
  }

  /**
   * PATCHes a transit program driver with active and preferred statuses
   *
   * @param transitProgramDriver
   * @param active
   * @param preferred
   * @permission driver_management
   */
  patchTransitProgramDriver(
    transitProgramDriver: TransitProgramDriver,
    active: boolean,
    preferred: boolean
  ): Observable<TransitProgramDriver> {
    const patchDictionary = {
      transitProgramId: transitProgramDriver.transitProgramId,
      driver: {
        id: transitProgramDriver.driver.id
      },
      active: active,
      preferred: preferred
    };

    return this.patch(
      `${this.resourceUrl}${transitProgramDriver.transitProgramId}/drivers/${transitProgramDriver.driver.id}`,
      patchDictionary
    ).pipe(map(response => Deserialize(response, TransitProgramDriver)));
  }

  /**
   * @param id
   * @param operatingSchedules
   * @permission operating_schedule_management
   */
  setOperatingSchedules(id: number, operatingSchedules: Array<OperatingSchedule>): Observable<Array<OperatingSchedule>> {
    return this.put<Array<OperatingSchedule>>(`${this.resourceUrl}${id}/operatingSchedules/`, Serialize(operatingSchedules)).pipe(
      map(response => Deserialize(response, OperatingSchedule))
    );
  }
  /**
   * Gets all geofences for a transitprogram
   *
   * @param transitProgramId
   * @permission geofence_management
   */
  listGeofencesForTransitProgram(transitProgramId: number): Observable<Array<Geofence>> {
    return this.get(`${this.resourceUrl}${transitProgramId}/geofences/`);
  }

  /**
   * Creates a geofence for a transit program
   *
   * @param transitProgramId
   * @param geofence
   * @permission geofence_management
   */
  addGeofence(transitProgramId: number, geofence: Geofence): Observable<Geofence> {
    return this.post(`${this.resourceUrl}${transitProgramId}/geofences/`, Serialize(geofence)).pipe(
      map(response => Deserialize(response, Geofence))
    );
  }

  /**
   * Removes a geofence for a transit program
   *
   * @param transitProgramId
   * @param geofence
   * @permission geofence_management
   */
  deleteGeofence(transitProgramId: number, geofenceid: number): Observable<boolean> {
    return this.delete(`${this.resourceUrl}${transitProgramId}/geofences/${geofenceid}`);
  }

  /**
   * Updates a geofence for a transit program
   *
   * @param transitProgramId
   * @param geofence
   * @permission geofence_management
   */
  updateGeofence(transitProgramId: number, geofence: Geofence): Observable<Geofence> {
    return this.put(`${this.resourceUrl}${transitProgramId}/geofences/${geofence.id}`, Serialize(geofence)).pipe(
      map(response => Deserialize(response, Geofence))
    );
  }

  /**
   * @param id
   * @permission transit_program_read
   */
  listRideMetadataFieldsForTransitProgram(id: number): Observable<Array<RideMetadataField>> {
    return this.get<Array<RideMetadataField>>(`${this.resourceUrl}${id}/rideMetadataFields/`).pipe(
      map(response => Deserialize(response, RideMetadataField))
    );
  }

  /**
   * @param transitProgramId
   * @param rideMetadataField
   * @permission system_configuration
   */
  addRideMetadataField(transitProgramId: number, rideMetadataField: RideMetadataField): Observable<RideMetadataField> {
    return this.post<RideMetadataField>(`${this.resourceUrl}${transitProgramId}/rideMetadataFields/`, Serialize(rideMetadataField)).pipe(
      map(response => Deserialize(response, RideMetadataField))
    );
  }

  /**
   * @param transitProgramId
   * @param rideMetadataFieldId
   * @param rideMetadataField
   * @permission system_configuration
   */
  updateRideMetadataField(
    transitProgramId: number,
    rideMetadataFieldId: number,
    rideMetadataField: RideMetadataField
  ): Observable<RideMetadataField> {
    return this.put<RideMetadataField>(
      `${this.resourceUrl}${transitProgramId}/rideMetadataFields/${rideMetadataFieldId}`,
      Serialize(rideMetadataField)
    ).pipe(map(response => Deserialize(response, RideMetadataField)));
  }

  /**
   * @param transitProgramId
   * @param rideMetadataFieldId
   * @permission system_configuration
   */
  deleteRideMetadataField(transitProgramId: number, rideMetadataFieldId: number): Observable<boolean> {
    return this.delete<boolean>(`${this.resourceUrl}${transitProgramId}/rideMetadataFields/${rideMetadataFieldId}`);
  }

  /**
   * @param transitProgramId
   * @permission operating_schedule_management
   */
  listHolidaySchedule(transitProgramId: number, startDate: Date, endDate: Date): Observable<Array<HolidaySchedule>> {
    const urlStartDate = moment(startDate).format('YYYY-MM-DD');
    const urlEndDate = moment(endDate).format('YYYY-MM-DD');
    return this.get<Array<HolidaySchedule>>(`${this.resourceUrl}${transitProgramId}/holidaySchedules/`, {
      startDate: urlStartDate,
      endDate: urlEndDate
    }).pipe(map(response => Deserialize(response, HolidaySchedule)));
  }

  /**
   * @param transitProgramId
   * @param holidaySchedule
   * @permission operating_schedule_management
   */
  addHolidaySchedule(transitProgramId: number, holidaySchedules: Array<HolidaySchedule>): Observable<HolidaySchedule> {
    return this.post(`${this.resourceUrl}${transitProgramId}/holidaySchedules/`, Serialize(holidaySchedules)).pipe(
      map(response => Deserialize(response, HolidaySchedule))
    );
  }

  /**
   * @param transitProgramId
   * @param holidaySchedule
   * @permission operating_schedule_management
   */
  updateHolidaySchedule(transitProgramId: number, holidaySchedule: HolidaySchedule): Observable<HolidaySchedule> {
    return this.put<HolidaySchedule>(
      `${this.resourceUrl}${transitProgramId}/holidaySchedules/${holidaySchedule.id}`,
      Serialize(holidaySchedule)
    ).pipe(map(response => Deserialize(response, HolidaySchedule)));
  }

  /**
   * @param transitProgramId
   * @param holidayScheduleId
   * @permission operating_schedule_management
   */
  deleteHolidaySchedule(transitProgramId: number, holidayScheduleId: number): Observable<boolean> {
    return this.delete<boolean>(`${this.resourceUrl}${transitProgramId}/holidaySchedules/${holidayScheduleId}`);
  }

  /**
   * @permission client_fee_schedule_read
   */
  listFareCalculators(): Observable<Array<FareCalculator>> {
    return this.get<Array<FareCalculator>>(`${this.resourceUrl}fareCalculators/`).pipe(
      map(response => Deserialize(response, FareCalculator))
    );
  }

  /**
   * @param transitProgramId
   * @param serviceLevelId
   * @permission client_fee_schedule_read
   */
  findActiveClientFeeSchedule(transitProgramId: number, serviceLevelId: number): Observable<ApprovalObjectStatus> {
    return this.get(`${this.resourceUrl}${transitProgramId}/clientFeeSchedule/serviceLevel/${serviceLevelId}/active`).pipe(
      map(response => Deserialize(response, ApprovalObjectStatus))
    );
  }

  /**
   * @param transitProgramId
   * @param clientFeeScheduleId
   * @permission client_fee_schedule_read
   */
  findClientFeeScheduleById(transitProgramId: number, clientFeeScheduleId: number): Observable<ApprovalObjectStatus> {
    return this.get(`${this.resourceUrl}${transitProgramId}/clientFeeSchedule/${clientFeeScheduleId}`).pipe(
      map(response => Deserialize(response, ApprovalObjectStatus))
    );
  }

  /**
   * @param transitProgramId
   * @param clientFeeScheduleId
   * @permission client_fee_schedule_read
   */
  findClientFeeScheduleInfoById(transitProgramId: number, clientFeeScheduleId: number): Observable<ClientFeeScheduleApprovalObjectInfo> {
    return this.get(`${this.resourceUrl}${transitProgramId}/clientFeeScheduleInfo/${clientFeeScheduleId}`).pipe(
      map(response => Deserialize(response, ClientFeeScheduleApprovalObjectInfo))
    );
  }

  /**
   * Removes client fee schedule
   *
   * @param transitProgramId
   * @param clientFeeScheduleId
   * @permission client_fee_schedule_read
   */
  deleteClientFeeSchedule(transitProgramId: number, clientFeeScheduleId: number): Observable<ClientFeeSchedule> {
    return this.delete(`${this.resourceUrl}${transitProgramId}/clientFeeSchedule/${clientFeeScheduleId}`);
  }

  /**
   * @permission client_fee_schedule_read
   */
  listAllClientFeeSchedules(transitProgramId: number, serviceLevelId: number): Observable<Array<ClientFeeScheduleApprovalStatus>> {
    return this.get<Array<ClientFeeScheduleApprovalStatus>>(
      `${this.resourceUrl}${transitProgramId}/clientFeeSchedule/serviceLevel/${serviceLevelId}`
    ).pipe(map(response => Deserialize(response, ClientFeeScheduleApprovalStatus)));
  }

  /**
   *
   * @param transitProgramId
   * @param clientFeeScheduleId
   * @param clientFeeSchedule
   * @permission client_fee_schedule_update
   */
  updateClientFeeSchedule(
    transitProgramId: number,
    clientFeeScheduleId: number,
    clientFeeSchedule: ClientFeeSchedule
  ): Observable<ClientFeeSchedule> {
    return this.put<ClientFeeSchedule>(
      `${this.resourceUrl}${transitProgramId}/clientFeeSchedule/${clientFeeScheduleId}`,
      Serialize(clientFeeSchedule)
    ).pipe(map(response => Deserialize(response, ClientFeeSchedule)));
  }

  /**
   *
   * @param transitProgramId
   * @param approvalRequestSubmission
   */
  createNewClientFeeSchedule(
    transitProgramId: number,
    approvalRequestSubmission: ApprovalRequestSubmission
  ): Observable<ClientFeeSchedule> {
    return this.post<ApprovalRequestSubmission>(
      `${this.resourceUrl}${transitProgramId}/clientFeeSchedule/`,
      Serialize(approvalRequestSubmission)
    ).pipe(map(response => Deserialize(response, ClientFeeSchedule)));
  }

  /************************************* Vehicle Inspection ***********************/

  listTransitProgramVehicleInspectionItems(transitProgramId: number): Observable<Array<TransitProgramVehicleInspectionItem>> {
    return this.get<Array<TransitProgramVehicleInspectionItem>>(`${this.resourceUrl}${transitProgramId}/vehicleInspectionItems/`).pipe(
      map(response => Deserialize(response, TransitProgramVehicleInspectionItem))
    );
  }
}
